import withDisplayName from 'bz-utilities/hoc/display-name';
import type { ICrossTabEvent } from 'bz-utilities/modules/plugins/register/stores/mediator';
import type { Constructable } from 'bz-utilities/ts';
import ContactRefreshEvent from 'features/base/contacts/shared/layout/contact-refresh-event';
import React from 'react';
import { cache } from 'tm3-core/cache';
import { CacheUpdateEvent } from 'tm3-core/events/cache';

const channelName = 'bz>mediator>cross-tab';
const eventRegistry: Array<Constructable<ICrossTabEvent>> = [ContactRefreshEvent, CacheUpdateEvent];

const ContactRefreshSubscriber = React.memo(
  withDisplayName(() => {
    const tabId = React.useRef(`${Date.now()}-${Math.random()}`);
    const channelRef = React.useRef<BroadcastChannel | null>(null);

    React.useEffect(() => {
      if (typeof BroadcastChannel === 'undefined') {
        cache.mediator.setCrossTabPublisher(undefined);
        return;
      }

      const instance = new BroadcastChannel(channelName);
      channelRef.current = instance;

      instance.onmessage = async (
        event: MessageEvent<{ source?: string; type?: string; payload?: Record<string, unknown> }>
      ) => {
        if (!event.data || event.data.source === tabId.current || !event.data.type) return;

        const eventType = eventRegistry.find((x) => x.name === event.data.type);
        if (!eventType) return;

        const reconstructed = new eventType(event.data.payload ?? {});
        await cache.mediator.publish(reconstructed, { skipCrossTab: true });
      };

      cache.mediator.setCrossTabPublisher((event) => {
        if (!channelRef.current) return;
        channelRef.current.postMessage({
          source: tabId.current,
          type: event.constructor.name,
          payload: event
        });
      });

      return () => {
        if (channelRef.current === instance) {
          channelRef.current = null;
        }
        cache.mediator.setCrossTabPublisher(undefined);
        instance.close();
      };
    }, []);

    return null;
  }, 'ContactRefreshSubscriber')
);

export default ContactRefreshSubscriber;
